import axios, { type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import { readCsrfToken, CSRF_HEADER, needsCsrf } from "@/lib/csrf";
import {
  createOfflineCacheKey,
  notifyOfflineCacheUsed,
  readOfflineCache,
  writeOfflineCache,
} from "@/lib/offline-cache";
import {
  queueOfflineMutation,
  type OfflineMutationMethod,
} from "@/lib/offline-outbox";
import { getApiOrigin, isNative } from "@/lib/platform";
import { isOnline } from "@/lib/connectivity";
import { hasKioskSession, reloginKiosk } from "@/lib/kiosk-session";
import { useAuthStore, type AuthUser } from "@/store/auth-store";
import { isModuleNotInPlanBody, reportModuleNotInPlan } from "@/lib/module-access-events";
import { isSchoolScopeRefusal, overseerHasNoSchoolScope } from "@/lib/school-scope-refusal";
import { assertModuleEnabledForUrl } from "@/lib/module-request-guard";
import { noteApiFailure } from "@/lib/app-review";

const STORAGE_KEY = "campushubai-auth";

// Base URL resolution.
//
// On the web we keep the historical behaviour: `/api/v1` resolves to
// either the Vite dev proxy (dev) or the same-origin nginx route (prod).
//
// Inside a Capacitor shell, the WebView origin is `https://localhost`,
// so a relative URL would never reach our API. `getApiOrigin()`
// returns the absolute origin (from `VITE_API_URL`) — we just append
// the `/api/v1` suffix.
const API_ORIGIN = getApiOrigin();

export const API_BASE_URL = (() => {
  if (!API_ORIGIN) return "/api/v1";
  return API_ORIGIN.endsWith("/api") ? `${API_ORIGIN}/v1` : `${API_ORIGIN}/api/v1`;
})();

// Capacitor's native bridge serves the app from `https://localhost`,
// which is a different origin than our API. Setting cookies cross-site
// from a WebView requires SameSite=None+Secure on the API; we still
// send `withCredentials` so refresh cookies work, but every request
// also carries the access token as a Bearer header (which is the
// canonical path on native). Net effect: cookies are best-effort
// fallback, headers are authoritative.
const NATIVE = isNative();

interface StoredState {
  state?: {
    token?: string | null;
    refreshToken?: string | null;
    activeSchoolId?: string | null;
  };
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  /**
   * Set by a caller that renders the failure itself (a resolution dialog, an
   * inline panel) and does not want the interceptor's toast on top of it.
   *
   * The interceptor's job is to make sure no failure passes unmentioned, which
   * is right for the ninety-nine call sites that just `await` and move on. But
   * where the response body is the input to a UI the user is about to work in —
   * the timetable publish 409, whose `conflicts[]` drive a fix-it-here dialog —
   * a toast repeating the same sentence over the top of it is noise the user has
   * to dismiss before they can read the thing that actually helps.
   */
  suppressErrorToast?: boolean;
}

interface RefreshResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken?: string;
    user: AuthUser;
  };
}

export type OfflineQueued<T> = T & { __offlineQueued?: true };

export function isOfflineQueued<T>(value: T): value is OfflineQueued<T> {
  return typeof value === "object" && value !== null && "__offlineQueued" in value;
}

export function getStoredAccessToken(): string | null {
  return getToken();
}

/**
 * The persisted session, read the way the auth store persists it: the
 * `sessionStorage` box first ("Keep me signed in" unticked), then
 * `localStorage`. These readers used to look at `localStorage` only, so a
 * session-only sign-in sent every request without a Bearer header, took a 401,
 * refreshed and retried — a rotation per request batch, and three round trips
 * for one. Exported for tests.
 */
export function readPersistedAuthState(): StoredState["state"] | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (!raw) {
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      raw = null;
    }
  }
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as StoredState)?.state ?? null;
  } catch {
    return null;
  }
}

function getToken(): string | null {
  return readPersistedAuthState()?.token ?? null;
}

function getActiveSchoolId(): string | null {
  const id = readPersistedAuthState()?.activeSchoolId ?? null;
  // "platform" is the overseer placeholder, never a school to scope to.
  return id && id !== "platform" ? id : null;
}

function getStoredRefreshToken(): string | null {
  return readPersistedAuthState()?.refreshToken ?? null;
}

export function getCurrentUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

export function getCurrentSchoolId(): string | null {
  return getActiveSchoolId() ?? useAuthStore.getState().user?.school?.id ?? null;
}

export function isNetworkUnavailable(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  // Native-aware: on a Capacitor WebView `navigator.onLine` can be stuck
  // `false` even while connected, so lean on the actual error shape (no
  // response / network error / timeout) as the primary signal.
  return (
    !isOnline()
  ) || !error.response || error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    // Stamp every request with the client surface so the API can
    // segment metrics (web vs ios vs android) and apply per-platform
    // rate limiting or feature flags without sniffing User-Agent.
    "X-Client": NATIVE ? "native" : "web",
  },
  // Needed so the browser sends the httpOnly refresh cookie on
  // `/auth/refresh` and so we can round-trip the CSRF cookie on
  // mutations. On native this is a no-op for cross-origin requests
  // unless the API ships SameSite=None+Secure cookies — in which case
  // the WebView's cookie jar honours it. The Bearer header is the
  // authoritative auth path either way.
  withCredentials: true,
});

export const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Client": NATIVE ? "native" : "web",
  },
  withCredentials: true,
});

let refreshPromise: Promise<string> | null = null;

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const schoolId = getActiveSchoolId();
  const requestSchoolId = config.headers["X-School-Id"];
  if (schoolId && !requestSchoolId) {
    config.headers["X-School-Id"] = schoolId;
  }
  if (needsCsrf(config.method)) {
    const csrf = readCsrfToken();
    if (csrf) config.headers[CSRF_HEADER] = csrf;
  }
  return config;
});

/**
 * True when `url` targets our own API (relative `/api/…`, or the API origin).
 * Exported for tests. Anything else — S3 presigned URLs, weather, maps — must
 * never receive our CSRF token.
 */
export function isOwnApiUrl(url: string | undefined, base: string = API_BASE_URL): boolean {
  if (!url) return false;
  if (url.startsWith("/api/")) return true;
  try {
    const here = typeof window !== "undefined" ? window.location.href : "http://localhost/";
    const target = new URL(url, here);
    const api = new URL(base, here);
    return target.origin === api.origin && target.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

/**
 * Echo the CSRF cookie on EVERY state-changing axios request to our API, not
 * just those made through `apiClient`. Several auth-adjacent calls use bare
 * `axios.post(..., { withCredentials: true })` (switch role, PIN setup, forced
 * password change, OTP verify, kiosk session, handoff, Control Center login).
 * Since the CSRF plugin actually runs (2026-09-26), any of those made while a
 * refresh cookie exists — a multi-role user switching Teacher → Principal is
 * the common case — was refused "Invalid or missing CSRF token."
 */
export function installGlobalCsrfHeader(instance = axios): void {
  instance.interceptors.request.use((config) => {
    if (needsCsrf(config.method) && isOwnApiUrl(config.url) && !config.headers[CSRF_HEADER]) {
      const csrf = readCsrfToken();
      if (csrf) config.headers[CSRF_HEADER] = csrf;
    }
    return config;
  });
}

/**
 * The `fetch` twin of `installGlobalCsrfHeader`: streaming / binary calls
 * (student import NDJSON, Hubba TTS, UDISE export) use raw `fetch`, which no
 * axios interceptor sees. Wrapping it once means a new raw fetch to our API
 * can't reintroduce "Invalid or missing CSRF token." Only non-GET requests to
 * our own API get the header; a caller-set header is left alone.
 */
export function installGlobalFetchCsrf(target: { fetch: typeof fetch } = globalThis): void {
  const original = target.fetch as (typeof fetch & { __csrfWrapped?: boolean }) | undefined;
  if (!original || original.__csrfWrapped) return;
  const bound = original.bind(target);
  const wrapped = ((input: RequestInfo | URL, init?: RequestInit) => {
    const isRequest = typeof Request !== "undefined" && input instanceof Request;
    const method = init?.method ?? (isRequest ? (input as Request).method : "GET");
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
    if (needsCsrf(method) && isOwnApiUrl(url)) {
      const csrf = readCsrfToken();
      if (csrf) {
        const headers = new Headers(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
        if (!headers.has(CSRF_HEADER)) {
          headers.set(CSRF_HEADER, csrf);
          return bound(input, { ...init, headers });
        }
      }
    }
    return bound(input, init);
  }) as typeof fetch & { __csrfWrapped?: boolean };
  wrapped.__csrfWrapped = true;
  target.fetch = wrapped;
}

// Response interceptor — handle 401 by clearing auth state and redirecting,
// and show a toast for all other non-401 errors
// Exported so non-axios callers (e.g. the streaming NDJSON student-import
// fetch in use-students.ts) can refresh a stale access token and retry,
// instead of failing with a raw 401 mid-import. Shares the same dedup
// `refreshPromise` so concurrent callers coalesce onto one /auth/refresh.
/**
 * Why a refresh attempt failed.
 *
 * This distinction is the whole ballgame for "the app keeps logging me out".
 * A failed `/auth/refresh` has two completely different meanings:
 *
 *   • `sessionDead: true`  — the SERVER looked at the token and rejected it
 *     (401/403). The session really is over; signing out is correct.
 *
 *   • `sessionDead: false` — we never got a verdict. Offline, flaky mobile
 *     link, request timeout, 429 from a shared rate-limit bucket, 502/503
 *     while the API redeploys or Neon cold-starts, Redis blip. The session is
 *     almost certainly still valid.
 *
 * Previously every one of these collapsed into "log the user out and hard-
 * redirect to /login", so a two-second tunnel or a single 503 evicted people
 * mid-task — repeatedly, across app, kiosk and web. Now only an affirmative
 * rejection ends a session; everything else leaves it intact and lets the
 * caller retry or fall back to cache.
 */
export class SessionRefreshError extends Error {
  readonly sessionDead: boolean;
  readonly status?: number;

  constructor(sessionDead: boolean, status?: number, cause?: unknown) {
    super(sessionDead ? "Session rejected by server" : "Could not reach the session service");
    this.name = "SessionRefreshError";
    this.sessionDead = sessionDead;
    this.status = status;
    this.cause = cause;
  }
}

export function isSessionDead(error: unknown): boolean {
  return error instanceof SessionRefreshError && error.sessionDead;
}

// Exported for tests — this predicate decides whether a user gets signed out,
// so it is worth pinning down case by case.
export function classifyRefreshFailure(error: unknown): SessionRefreshError {
  if (!axios.isAxiosError(error)) return new SessionRefreshError(false, undefined, error);

  const status = error.response?.status;

  // No response at all → we never reached a verdict. Keep the session.
  if (!error.response) return new SessionRefreshError(false, undefined, error);

  // Only an explicit rejection of the credential ends the session. Note 429 and
  // 5xx are deliberately NOT session-ending, and neither is the server's
  // REFRESH_TOKEN_ROTATED grace response — that one means "your current token
  // is still good, this attempt just raced a rotation".
  const code = (error.response.data as { error?: { code?: string } })?.error?.code;
  if (status === 401 && code === "REFRESH_TOKEN_ROTATED") {
    return new SessionRefreshError(false, status, error);
  }
  const dead = status === 401 || status === 403;
  return new SessionRefreshError(dead, status, error);
}

// A refresh must never hang forever: SessionRestorer blocks the whole app on
// this promise, so an un-timeboxed request on a dead link leaves users staring
// at the splash screen instead of the (perfectly usable, cached) app.
const REFRESH_TIMEOUT_MS = 15_000;

/**
 * A refresh whose response never arrived (timeout, reset, a cold container
 * answering 502/503 for a moment) is retried ONCE after a short pause, inside
 * the same single-flight promise. The server may well have rotated on the
 * attempt we gave up on; its reuse window answers the retry with that same
 * successor, so the retry converges instead of the next 401 — minutes later —
 * presenting a token the server already considers spent. 429 and every
 * authoritative verdict are not retried. Exported for tests.
 */
export const REFRESH_RETRY_DELAY_MS = 1_500;
export function shouldRetryRefresh(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}

function postRefresh(): Promise<string> {
  const csrf = readCsrfToken();
  const legacyRefreshToken = getStoredRefreshToken();
  return refreshClient
    .post<RefreshResponse>(
      "/auth/refresh",
      legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {},
      {
        timeout: REFRESH_TIMEOUT_MS,
        ...(csrf ? { headers: { [CSRF_HEADER]: csrf } } : {}),
      },
    )
    .then((response) => {
      const { accessToken, refreshToken, user } = response.data.data;
      useAuthStore.getState().applySession({ user, token: accessToken, refreshToken });
      return accessToken;
    });
}

export async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = postRefresh()
      .catch(async (err: unknown) => {
        if (!shouldRetryRefresh(err) || !isOnline()) throw err;
        await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
        return postRefresh();
      })
      .catch((err: unknown) => {
        throw classifyRefreshFailure(err);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Public, pre-session auth endpoints whose 401/403 is a credential verdict,
// not an expired session — see the note in the response interceptor. A single
// prefix like `/auth/login` covers the whole family (`/auth/login/verify`,
// `/auth/login/otp/request`, `/auth/login/otp/verify`).
const AUTH_CHALLENGE_PATHS = [
  "/auth/login",
  "/auth/device-login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  // Public invitation preview/accept. A 401 from accept means "that password
  // or PIN doesn't match the existing account" — a credential verdict, not an
  // expired session (DEFECT fixed: it used to refresh, log out and hard-reload).
  "/invitations/token/",
];

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      const isUnauthorized = error.response?.status === 401;
      const isRefreshRequest = originalRequest?.url?.includes("/auth/refresh") ?? false;

      // The store-rating prompt stays quiet for two minutes after anything
      // failed (lib/app-review.ts). A cancelled request is not a failure.
      if (error.code !== "ERR_CANCELED") noteApiFailure(error.response?.status);

      // Requests to the public auth endpoints (login, 2FA/OTP verify, device
      // login, register, password reset) are UNAUTHENTICATED by nature, and a
      // 401/403 from them is the endpoint's own verdict on the *submitted
      // credentials* — "invalid password", "wrong OTP", "dead device token" —
      // NOT an expired session. Running the refresh-and-retry / sign-out
      // machinery on them is wrong twice over: there is no session to refresh,
      // and the forced `window.location.href = "/login"` hard-reloads the page,
      // throwing away the error message the login screen was about to render.
      // That is exactly the "login page just refreshes, no 'invalid
      // credentials' message" defect (CAMP-2026-0431) — it only bit the native
      // app because native-login posts through `apiClient` (the web login uses
      // a bare axios instance with no interceptor). Let these fall straight
      // through to the caller's catch block so it can show the error inline.
      const isAuthChallenge = AUTH_CHALLENGE_PATHS.some(
        (p) => originalRequest?.url?.includes(p) ?? false,
      );

      if (
        isUnauthorized &&
        originalRequest &&
        !originalRequest._retry &&
        !isRefreshRequest &&
        !isAuthChallenge
      ) {
        originalRequest._retry = true;

        const applyAuthAndRetry = (token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          const csrf = readCsrfToken();
          if (needsCsrf(originalRequest.method) && csrf) {
            originalRequest.headers[CSRF_HEADER] = csrf;
          }
          return apiClient(originalRequest);
        };

        // 1. Normal path: refresh the access token and retry.
        let refreshFailure: unknown;
        try {
          return await applyAuthAndRetry(await refreshAccessToken());
        } catch (err) {
          refreshFailure = err;
          /* fall through to kiosk re-login / logout */
        }

        // 2. Kiosk terminals never bounce to /login. If the refresh token is
        //    dead (device off for weeks, cold reboot), re-establish the
        //    session transparently from the stored kiosk credentials and
        //    retry. If that also fails (offline, or password rotated), keep
        //    the session in place and let the caller fall back to cache /
        //    the offline outbox — the operator stays on the kiosk.
        if (hasKioskSession()) {
          try {
            return await applyAuthAndRetry(await reloginKiosk());
          } catch {
            return Promise.reject(error);
          }
        }

        // 3. Only sign out when the server AFFIRMATIVELY rejected the session.
        //    A transient failure (offline, timeout, 429, 5xx, rotation race)
        //    must leave the session alone — the request fails, React Query
        //    retries, the offline cache covers reads, and the user stays where
        //    they were. Treating those as "session gone" is what was evicting
        //    people from the app several times a day.
        if (isSessionDead(refreshFailure)) {
          await useAuthStore.getState().logout();
          window.location.href = "/login";
        } else {
          console.warn(
            "[apiClient] keeping session after a non-authoritative refresh failure",
            refreshFailure,
          );
        }
        return Promise.reject(error);
      }

      if (isUnauthorized && !isAuthChallenge) {
        // A 401 we couldn't retry — either the refresh request itself, or a
        // request that already retried once with a freshly-minted token and was
        // still rejected. Kiosks always stay put. For everyone else this is an
        // authoritative rejection from the API (we held a valid-looking token
        // and the server said no), so signing out is correct — but never when
        // we're offline, where a 401 can't be trusted to mean anything.
        if (!hasKioskSession() && isOnline()) {
          await useAuthStore.getState().logout();
          window.location.href = "/login";
        }
      } else if (error.response) {
        // 403 responses are always role-gates in this codebase — every
        // route returns 403 either as a `success:false / error:{...}`
        // role-mismatch ("Restricted to CHAIRMAN", "Overseer roles
        // only", "You are not assigned to this school", etc.) or as a
        // plain authorize() rejection from the Fastify plugin. The
        // shape of the body is irrelevant; from the caller's point of
        // view a 403 means "the active role isn't allowed here", which
        // is an internal-state signal, not a user-facing error.
        //
        // The earlier suppression keyed on
        // `code === "FORBIDDEN"`, but the dashboard chairman routes
        // also surface a couple of 403s with different codes / no
        // code at all (the Fastify authorize() plugin returns a plain
        // "Forbidden" string), and those slipped through to a toast.
        // CAMP-2026-0095 — broaden the suppression to *any* 403 so the
        // class of bug can't resurface no matter what shape a future
        // route returns. We still log a breadcrumb so a developer can
        // find the missing hook-side gate.
        const status = error.response?.status;
        // The school switched this module off (or a stale menu raced the
        // change). Refetch the entitlements so the surface that led here
        // disappears, and say so once, calmly, rather than leaving an empty
        // card behind. Callers that render their own failure keep the toast off.
        if (isModuleNotInPlanBody(status, error.response?.data)) {
          reportModuleNotInPlan({ toast: !originalRequest?.suppressErrorToast });
        }
        if (isModuleNotInPlanBody(status, error.response?.data)) {
          // handled above: no generic toast for this one
        } else if (originalRequest?.suppressErrorToast) {
          console.warn(
            `[apiClient] not toasting ${status} from ${originalRequest?.url ?? "unknown URL"} — the caller renders this failure itself.`,
          );
        } else if (
          isSchoolScopeRefusal(status, error.response?.data) &&
          overseerHasNoSchoolScope(useAuthStore.getState().user, getActiveSchoolId())
        ) {
          // An overseer at All schools asked a school-scoped route a question it
          // can only answer for ONE school. Not actionable, and the page already
          // says "pick a school" — a red toast over the open sheet is just noise.
          console.warn(
            `[apiClient] swallowed school-scope refusal from ${originalRequest?.url ?? "unknown URL"} — gate the calling hook on a picked school to avoid the wasted request.`,
          );
        } else if (status === 403) {
          const code =
            (error.response?.data as { error?: { code?: string } })?.error?.code ?? "FORBIDDEN";
          console.warn(
            `[apiClient] swallowed 403 ${code} from ${originalRequest?.url ?? "unknown URL"} — gate the calling hook on role to avoid the wasted request.`,
          );
        } else if (status === 429) {
          // The shared per-IP budget is spent. One page can have a dozen
          // queries in flight, and each used to toast the server's raw
          // "Too many requests" — a stack of red cards for one cause. Say it
          // once, calmly; query-client.ts already backs the retry off.
          notifyRateLimited();
        } else {
          const message =
            (error.response?.data as { error?: { message?: string } })?.error
              ?.message ?? "Request failed";
          // Framework "route not found" 404s are an internal signal, never a
          // user-facing message: they surface when a static web deploy ships a
          // caller before the API rollout registers the route, or when a
          // best-effort/feature-flagged endpoint is absent. Toasting the raw
          // "Route GET /api/v1/... not found" string leaks an internal path to
          // the user (CAMP-2026-0135). Business 404s ("Student not found") use a
          // human message and still toast. Swallow only the route-not-found
          // shape, leaving a breadcrumb for developers.
          const isRouteNotFound =
            status === 404 && /^Route\s+\S+\s+\S+\s+not found$/.test(message);
          // CAMP-2026-0352 — a business 404 ("Exam not found.", "Student not
          // found") on a GET is almost always a background react-query refetch
          // racing a delete: the row was just removed, the invalidate/remove
          // triggers one last fetch of the dead id, the API answers 404, and a
          // red toast lands on top of the "… deleted" success. The read is not
          // actionable — the page renders its own empty/not-found state — so
          // stay silent for GET 404s (same philosophy as the 5xx-GET branch
          // below). Writes (DELETE/POST/PATCH) still surface their 404s.
          const isReadNotFound =
            status === 404 && (originalRequest?.method ?? "get").toLowerCase() === "get";
          if (isRouteNotFound) {
            console.warn(
              `[apiClient] swallowed route-not-found from ${originalRequest?.url ?? "unknown URL"} — the API build may be rolling out, or the endpoint is gated/removed.`,
            );
          } else if (isReadNotFound) {
            console.warn(
              `[apiClient] swallowed 404 from GET ${originalRequest?.url ?? "unknown URL"} (background read of a missing/deleted resource).`,
            );
          } else if (status === 409) {
            // A 409 Conflict is never a system failure — it's a
            // business-rule collision the user can resolve themselves
            // (an overlapping exam slot, a duplicate fee row, a name
            // already taken, …). The API always pairs it with a plain
            // human message, so surface it as a friendly, non-destructive
            // warning rather than a red error toast (CAMP-2026-0160).
            toast.warning(message);
          } else if (
            typeof status === "number" &&
            status >= 500 &&
            status <= 599 &&
            apiErrorCode(error)
          ) {
            // CAMP-2026-0292 / CAMP-2026-0293 — a 5xx that carries an explicit
            // `error.code` is this codebase's "dependency is down, and here is
            // what to do about it" shape (AI_UNAVAILABLE and friends, see
            // CAMP-2026-0158). Those messages are hand-written for the operator
            // — "AI credits are exhausted", "AI is rate-limited, try again in a
            // minute" — and burying them under the generic
            // "Something went wrong on our end" is what made every AI failure
            // indistinguishable from a crash and un-triageable from a ticket.
            //
            // Originally this keyed on `status === 503`, but ~20 routes answer
            // a coded **502** for exactly the same "upstream refused us" case
            // (SMS_GATEWAY_REJECTED, WHATSAPP_SEND_FAILED, IMAGE_GENERATION_-
            // FAILED, …) and every one of them was still collapsing into the
            // generic copy — the 0292 bug class, just one status code over.
            // An SMS broadcast that failed because the school's gateway toggle
            // is off would tell the admin only "Something went wrong on our
            // end", discarding the hand-written "Enable it in Settings →
            // Integrations → SMS Gateway" that the route bothered to write.
            // So: gate on the *code*, not the number. Requiring `error.code`
            // is what keeps this safe — a crashed handler or a raw Prisma
            // "Can't reach database server at ep-…neon.tech" has no code and
            // still falls through to the generic copy below, so nothing raw
            // can leak.
            toast.error(message);
          } else if (typeof status === "number" && status >= 500) {
            // Never surface a raw 5xx body — a DB-down / crashed handler can
            // dump internals (e.g. a full Prisma "Can't reach database server
            // at ep-...neon.tech" stack) straight into a toast, which both
            // scares the user and leaks infrastructure detail.
            //
            // And stay SILENT for GET reads: background hydration queries
            // (e.g. the kiosk's attendance-sessions lookup) routinely fail
            // when the DB is flaky or the device is offline, and the flow
            // still works from cache — a red toast per failed read is just
            // noise. Writes (POST/PATCH/…) still tell the user something broke.
            const isRead = (originalRequest?.method ?? "get").toLowerCase() === "get";
            if (isRead) {
              console.warn(
                `[apiClient] swallowed ${status} from ${originalRequest?.url ?? "unknown URL"} (background read).`,
              );
            } else {
              toast.error("Something went wrong on our end. Please try again in a moment.");
            }
          } else {
            toast.error(message);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

/** How long a 429 toast suppresses further 429 toasts. */
export const RATE_LIMIT_TOAST_DEDUPE_MS = 10_000;
export const RATE_LIMIT_TOAST_MESSAGE = "Too many requests — slowing down for a moment";
let lastRateLimitToastAt: number | null = null;

/** Show the 429 notice unless one was shown in the last RATE_LIMIT_TOAST_DEDUPE_MS. */
function notifyRateLimited(): void {
  const now = Date.now();
  if (lastRateLimitToastAt !== null && now - lastRateLimitToastAt < RATE_LIMIT_TOAST_DEDUPE_MS) return;
  lastRateLimitToastAt = now;
  toast.warning(RATE_LIMIT_TOAST_MESSAGE, { id: "rate-limited" });
}

/** Test seam: forget when the last 429 toast was shown. */
export function resetRateLimitToastForTests(): void {
  lastRateLimitToastAt = null;
}

/** The `error.code` an API response carried, if any. */
export function apiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  return (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
}

/** The `error.field` an API validation response named (which input to pin the message to), if any. */
export function apiErrorField(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  return (error.response?.data as { error?: { field?: string } } | undefined)?.error?.field;
}

/**
 * True when the API actually answered with an error status — i.e. the response
 * interceptor above has already shown a toast for it. Handlers that add their
 * own `toast.error` in a catch block should skip it in that case; otherwise the
 * user gets two stacked toasts for one failure, and the less useful of the two
 * (the call site's generic fallback) is usually the one they read.
 */
export function isApiResponseError(error: unknown): boolean {
  return axios.isAxiosError(error) && Boolean(error.response);
}

/**
 * True only when the response interceptor above actually SHOWED a toast for this
 * error. `isApiResponseError` over-claims: the interceptor deliberately stays
 * silent on every 403, on "Route X not found" 404s, on background GET 404s, on
 * GET 5xx reads, on a school-scope refusal for an overseer with no school, on a
 * module-not-in-plan 403 (it raises its own throttled notice instead), and when
 * the request opted out with `suppressErrorToast`. A catch block that returns
 * early on `isApiResponseError` therefore swallows those failures completely.
 * Use this instead: `if (!apiErrorWasToasted(err)) toast.error(apiErrorMessage(...))`
 * toasts exactly the failures nobody else did, and never doubles up (e.g. 409).
 *
 * Keep in step with the branch order in the response interceptor.
 */
export function apiErrorWasToasted(error: unknown): boolean {
  if (!axios.isAxiosError(error) || !error.response) return false;
  const { status, data } = error.response;
  const config = error.config as RetryableRequestConfig | undefined;
  const url = config?.url ?? "";
  const isAuthChallenge = AUTH_CHALLENGE_PATHS.some((p) => url.includes(p));
  // A 401 on a non-credential route ends in the refresh / sign-out flow, which
  // shows no toast of its own — but the user is being moved to /login, so the
  // caller must not stack a failure message on top.
  if (status === 401 && !isAuthChallenge) return true;
  if (isModuleNotInPlanBody(status, data)) return true;
  if (config?.suppressErrorToast) return false;
  if (
    isSchoolScopeRefusal(status, data) &&
    overseerHasNoSchoolScope(useAuthStore.getState().user, getActiveSchoolId())
  ) {
    return false;
  }
  if (status === 403) return false;
  const message = (data as { error?: { message?: string } } | undefined)?.error?.message ?? "Request failed";
  const isGet = (config?.method ?? "get").toLowerCase() === "get";
  if (status === 404 && (/^Route\s+\S+\s+\S+\s+not found$/.test(message) || isGet)) return false;
  if (status === 409) return true;
  if (typeof status === "number" && status >= 500 && status <= 599) {
    // A coded 5xx toasts its own message; an uncoded one toasts the generic
    // copy for writes and stays silent for background reads.
    return apiErrorCode(error) !== undefined || !isGet;
  }
  return true;
}

/**
 * The human message the API sent, falling back to `fallback`.
 *
 * CAMP-2026-0292 / CAMP-2026-0293 — call sites that `catch` an axios error and
 * toast `err.message` show the transport string ("Request failed with status
 * code 502"), not the `{ error: { message } }` envelope the route went to the
 * trouble of writing. That is how a payload-too-large and an exhausted AI quota
 * ended up looking like the same unactionable failure. Use this in any handler
 * that renders its own error instead of relying on the interceptor's toast.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { error?: { message?: string } } | undefined)
      ?.error?.message;
    if (message && message.trim()) return message;
    // No envelope (edge-generated 5xx, dropped connection, CORS) — the axios
    // string is internal noise, so prefer the caller's own wording.
    return fallback;
  }
  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

// Typed helper functions — each returns response.data.data
export async function apiGet<T>(
  url: string,
  params?: Record<string, unknown>,
  /** `suppressErrorToast` for readers that render the failure themselves. */
  options?: {
    suppressErrorToast?: boolean;
    /**
     * Read ONE named school without touching the global active school
     * (`setActiveSchool` re-scopes the whole app). Sent as `X-School-Id` for
     * this request only; the cache key and scope follow it so two schools'
     * answers never share an offline entry. Omitted = today's behaviour.
     */
    schoolId?: string;
  },
): Promise<T> {
  // Module control: do not knock on a module the school switched off (see
  // lib/module-request-guard.ts). Silent, no toast, not retried.
  assertModuleEnabledForUrl(url);
  const scopeSchoolId = options?.schoolId ?? getCurrentSchoolId();
  const cacheKey = createOfflineCacheKey({
    url,
    params,
    userId: getCurrentUserId(),
    schoolId: scopeSchoolId,
  });

  // Offline: serve the cache DIRECTLY and never touch the network. Firing a
  // request on a dead link can hang for the OS TCP timeout (tens of seconds)
  // before it errors — which is exactly the "keeps loading" spinner on the
  // kiosk. Reading the cache first makes an offline hit instant, and a miss
  // throw immediately (retry is disabled offline) so the UI shows an empty
  // state at once instead of spinning. `refetchOnReconnect` refills on return.
  if (!isOnline()) {
    const cached = await readOfflineCache<T>(cacheKey);
    if (cached) {
      notifyOfflineCacheUsed({ key: cacheKey, url, savedAt: cached.savedAt });
      return cached.data;
    }
    throw new Error(`offline: no cached response for ${url}`);
  }

  try {
    const response = await apiClient.get<{ success: boolean; data: T }>(url, {
      params,
      ...(options?.suppressErrorToast ? { suppressErrorToast: true } : {}),
      ...(options?.schoolId ? { headers: { "X-School-Id": options.schoolId } } : {}),
    });
    await writeOfflineCache({
      key: cacheKey,
      url,
      scope: `${getCurrentUserId() ?? "anonymous"}:${scopeSchoolId ?? "default"}`,
      data: response.data.data,
      savedAt: Date.now(),
    });
    return response.data.data;
  } catch (error) {
    if (isNetworkUnavailable(error)) {
      const cached = await readOfflineCache<T>(cacheKey);
      if (cached) {
        notifyOfflineCacheUsed({ key: cacheKey, url, savedAt: cached.savedAt });
        return cached.data;
      }
    }
    throw error;
  }
}

// How long an offline mutation may hang before we treat the link as dead and
// queue it. Without this, axios has no timeout, so on a no-connectivity save the
// request stays pending forever: no success, no error, no toast — the write just
// silently posts if/when the network returns. This bounds the "nothing happened"
// window so the caller's onSuccess (and its "saved offline" toast) always fires.
const OFFLINE_MUTATION_TIMEOUT_MS = 12_000;

async function mutateOrQueue<T>(
  method: OfflineMutationMethod,
  url: string,
  data: unknown,
  offlineFallback: T,
): Promise<OfflineQueued<T>> {
  const queued = async (): Promise<OfflineQueued<T>> => {
    await queueOfflineMutation({
      method,
      url,
      data,
      userId: getCurrentUserId(),
      schoolId: getCurrentSchoolId(),
    });
    return { ...(offlineFallback as object), __offlineQueued: true } as OfflineQueued<T>;
  };

  // Known-offline (native-aware): don't fire a request that would just hang —
  // queue immediately so the user gets instant "saved offline" feedback rather
  // than a spinner that never resolves.
  if (!isOnline()) return queued();

  try {
    const response = await apiClient.request<{ success: boolean; data: T }>({
      method,
      url,
      data,
      timeout: OFFLINE_MUTATION_TIMEOUT_MS,
    });
    return response.data.data as OfflineQueued<T>;
  } catch (error) {
    // Covers going offline mid-flight and dead-but-"online" links (the timeout
    // above surfaces as ECONNABORTED, which isNetworkUnavailable treats as offline).
    if (isNetworkUnavailable(error)) return queued();
    throw error;
  }
}

export async function apiPost<T>(
  url: string,
  data?: unknown,
  /** `suppressErrorToast` for callers that render the failure themselves. */
  options?: {
    suppressErrorToast?: boolean;
    timeoutMs?: number;
    signal?: AbortSignal;
    /** Write ONE named school (`X-School-Id`) without touching the global active school. */
    schoolId?: string;
  },
): Promise<T> {
  const response = await apiClient.post<{ success: boolean; data: T }>(url, data, {
    ...(options?.suppressErrorToast ? { suppressErrorToast: true } : {}),
    ...(options?.timeoutMs ? { timeout: options.timeoutMs } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
    ...(options?.schoolId ? { headers: { "X-School-Id": options.schoolId } } : {}),
  } as Parameters<typeof apiClient.post>[2]);
  return response.data.data;
}

export async function apiPostOrQueue<T>(url: string, data: unknown, offlineFallback: T): Promise<OfflineQueued<T>> {
  return mutateOrQueue("POST", url, data, offlineFallback);
}

export async function apiPatch<T>(
  url: string,
  data?: unknown,
  /** `schoolId` writes ONE named school (`X-School-Id`) without touching the global active school. */
  options?: { schoolId?: string },
): Promise<T> {
  const response = options?.schoolId
    ? await apiClient.patch<{ success: boolean; data: T }>(url, data, { headers: { "X-School-Id": options.schoolId } })
    : await apiClient.patch<{ success: boolean; data: T }>(url, data);
  return response.data.data;
}

export async function apiPatchOrQueue<T>(url: string, data: unknown, offlineFallback: T): Promise<OfflineQueued<T>> {
  return mutateOrQueue("PATCH", url, data, offlineFallback);
}

export async function apiPut<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<{ success: boolean; data: T }>(url, data);
  return response.data.data;
}

export async function apiDelete<T>(url: string, options?: { suppressErrorToast?: boolean }): Promise<T> {
  const response = await apiClient.delete<{ success: boolean; data: T }>(
    url,
    (options?.suppressErrorToast ? { suppressErrorToast: true } : undefined) as Parameters<typeof apiClient.delete>[1],
  );
  return response.data.data;
}
